/**
 * Horizontal pill strip of favorited solutions for quick access.
 *
 * Renders nothing when favorites array is empty or no favorites match
 * the current catalog. Each pill is a one-click shortcut to that solution.
 */
import type { SolutionsCatalog } from '@reefapp/shared';

interface FavoritesStripProps {
  favorites: string[];
  solutions: SolutionsCatalog;
  onSelect(catalogKey: string): void;
}

export function FavoritesStrip({
  favorites,
  solutions,
  onSelect,
}: FavoritesStripProps) {
  if (favorites.length === 0) return null;

  // Map favorite slugs to their catalog keys
  const entries = Object.entries(solutions);
  const mapped = favorites
    .map((slug) => {
      const entry = entries.find(([, sol]) => sol.slug === slug);
      return entry ? { key: entry[0], name: entry[1].name } : null;
    })
    .filter((item): item is { key: string; name: string } => item !== null);

  if (mapped.length === 0) return null;

  return (
    <div className="mb-6 print:hidden">
      <span className="text-slate-400 text-xs block mb-2">Favorites:</span>
      <div className="flex flex-wrap gap-2">
        {mapped.map(({ key, name }) => (
          <button
            key={key}
            type="button"
            onClick={() => onSelect(key)}
            className="bg-cyan-900/40 text-cyan-300 border border-cyan-600/40 hover:bg-cyan-800/50 px-3 py-1 rounded-full text-sm font-semibold transition-colors"
          >
            {name}
          </button>
        ))}
      </div>
    </div>
  );
}
